/**
 * Reports and the dashboard's four figures — `modules/reports/routes.ts`.
 *
 * All reads. A client never writes a report: the sections are staff-entered
 * (`.docs/00-index.md` §6) and the two derived ones are computed at read time.
 */

import { useApiQuery } from '../hooks.js'
import { queryKeys } from '../keys.js'

/** `GET /api/clients/me/reports` — the archive: count, headlines, what is coming. */
export function useReportArchive() {
  return useApiQuery({
    key: queryKeys.reports.archive(),
    request: (api) => api.rest.clients.me.reports.$get(),
  })
}

/**
 * `GET /api/clients/me/reports/:period` — one month's document.
 *
 * Throws by default, and that is the right answer here: a month with no report is a
 * page that does not exist, and the route boundary says so. `inline` is for a caller
 * that wants to render the absence itself.
 */
export function useReport(period: string, options: { inline?: boolean } = {}) {
  return useApiQuery({
    key: queryKeys.reports.document(period),
    request: (api) => api.rest.clients.me.reports[':period'].$get({ param: { period } }),
    ...(options.inline === undefined ? {} : { inline: options.inline }),
  })
}

/** The PDF, by redirect through `GET /api/files/:id/download`. An href, not a hook. */
export function reportPdfHref(period: string): string {
  return `/api/clients/me/reports/${encodeURIComponent(period)}/pdf`
}

/** The invoice PDF, the same way. */
export function invoicePdfHref(ref: string): string {
  return `/api/clients/me/invoices/${encodeURIComponent(ref)}/pdf`
}

/** `GET /api/clients/me/metrics?period=` — the dashboard's four figures. */
export function useMetrics(period?: string) {
  return useApiQuery({
    key: queryKeys.reports.metrics(period),
    request: (api) =>
      api.rest.clients.me.metrics.$get({ query: period === undefined ? {} : { period } }),
  })
}
